import { t, type StaffLanguage } from './staffText'
import type { GroupMapping, Identity } from './types'

/**
 * The pure logic behind the Directory screen and Sign out: what is wrong with a
 * set of group mappings, what is wrong with a connection's address, and where a
 * proxy session's sign-out has to go.
 *
 * Every check here mirrors one the server also makes — org.NormaliseGroup,
 * org.ResolveGroups's rule that a row is a duplicate of another, and
 * directory.Config.Problems's rule about StartTLS — so the form can say what is
 * wrong before a click reaches the network, in the server's own words. The server
 * remains the authority: nothing here is a substitute for its answer, only a
 * quicker one.
 */

/** One row of the group mappings form: what the row says, keyed by the row and
 *  never by the group name, because two rows may legitimately name the same
 *  group — one giving it a role, one a branch. `position` is carried through
 *  from the server for a row that came from there; what is actually sent back
 *  on save is this array's own order (fromMappingRows), not this field. */
export interface MappingRow {
  key: string
  group: string
  gives: 'role' | 'branch'
  roleId?: string
  branchId?: string
  position?: number
}

/**
 * normaliseGroup is the form a group is matched in, mirroring org.NormaliseGroup
 * exactly: lower case, and the spaces trimmed around a comma or an equals sign —
 * except a comma that is escaped (`Smith\, John`), which stays part of its value.
 */
export function normaliseGroup(name: string): string {
  const parts: string[] = []
  let current = ''
  let escaped = false
  for (const ch of name.trim()) {
    if (escaped) {
      current += ch
      escaped = false
    } else if (ch === '\\') {
      current += ch
      escaped = true
    } else if (ch === ',') {
      parts.push(current)
      current = ''
    } else {
      current += ch
    }
  }
  parts.push(current)
  return parts
    .map((part) => {
      const at = part.indexOf('=')
      if (at < 0) return part.trim().toLowerCase()
      return (part.slice(0, at).trim() + '=' + part.slice(at + 1).trim()).toLowerCase()
    })
    .join(',')
}

/** How a row's group and what it gives are compared for a duplicate — the same
 *  key mappingKey builds on the server, in internal/api/directory.go. */
function rowKey(row: MappingRow): string {
  return normaliseGroup(row.group) + '|' + (row.gives === 'role' ? (row.roleId ?? '') : row.branchId ?? '')
}

/**
 * mappingProblems says what is wrong with the form, in the order a person reads
 * it: every empty group first, then every row missing what it gives, then every
 * pair (or run) of rows giving the same group the same thing twice. A row already
 * named for one problem is not named again for the next — an empty group's role
 * is not worth mentioning too.
 *
 * `quiet` names rows to say nothing about yet: a row just added and not yet typed
 * in. "Row 3 needs a group name" under a row the person has only just asked for
 * reads as a telling-off for something they were about to do. The screen passes
 * nothing once Save has been pressed, so the row is named then.
 */
export function mappingProblems(
  rows: MappingRow[],
  lang: StaffLanguage,
  quiet: ReadonlySet<string> = new Set(),
): string[] {
  const problems: string[] = []
  const settled = new Set<number>()
  rows.forEach((row, i) => {
    if (quiet.has(row.key)) settled.add(i)
  })

  rows.forEach((row, i) => {
    if (settled.has(i)) return
    if (row.group.trim() === '') {
      problems.push(t('directory.rowNeedsGroup', lang, { row: i + 1 }))
      settled.add(i)
    }
  })

  rows.forEach((row, i) => {
    if (settled.has(i)) return
    if (row.gives === 'role' && !row.roleId) {
      problems.push(t('directory.rowNeedsRole', lang, { row: i + 1 }))
      settled.add(i)
    } else if (row.gives === 'branch' && !row.branchId) {
      problems.push(t('directory.rowNeedsBranch', lang, { row: i + 1 }))
      settled.add(i)
    }
  })

  const byKey = new Map<string, number[]>()
  rows.forEach((row, i) => {
    if (settled.has(i)) return
    const list = byKey.get(rowKey(row)) ?? []
    list.push(i)
    byKey.set(rowKey(row), list)
  })
  for (const indices of byKey.values()) {
    if (indices.length < 2) continue
    problems.push(t('directory.rowsSame', lang, { rows: rowNumbers(indices, lang) }))
  }

  return problems
}

/** "1 and 2", or "1, 2 and 3" for a run longer than a pair. Never one: a
 *  duplicate takes two rows. */
function rowNumbers(indices: number[], lang: StaffLanguage): string {
  const numbers = indices.map((i) => i + 1)
  const last = numbers[numbers.length - 1]!
  return t('directory.and', lang, { list: numbers.slice(0, -1).join(', '), last })
}

/** toMappingRows turns what the server sent into rows the form can edit. Every
 *  row gets a freshly minted key — never the group name, which two rows may
 *  share — so `newKey` is the only thing that has to be unique. Rows come back
 *  in position order (the store's own `ORDER BY position`), which is also the
 *  order this array is in. */
export function toMappingRows(mappings: GroupMapping[], newKey: () => string): MappingRow[] {
  return mappings.map((m) => ({
    key: newKey(),
    group: m.group,
    gives: m.branchId ? 'branch' : 'role',
    roleId: m.roleId,
    branchId: m.branchId,
    position: m.position,
  }))
}

/** fromMappingRows is what the form sends back: the row's key is local to this
 *  screen and never travels. `position` is sent as the row's index in this
 *  array — the order the administrator has set, including by moveMappingRow —
 *  and the server renumbers 1..n from it. */
export function fromMappingRows(
  rows: MappingRow[],
): Array<{ group: string; roleId?: string; branchId?: string; position: number }> {
  return rows.map((row, i) =>
    row.gives === 'role'
      ? { group: row.group, roleId: row.roleId, position: i }
      : { group: row.group, branchId: row.branchId, position: i },
  )
}

/**
 * moveMappingRow reorders one role mapping relative to its neighbours, up or
 * down — a drag would do the same thing with more code. Only role mappings are
 * reordered: a branch mapping's position plays no part in ResolveGroups, which
 * always combines every matching branch regardless of order, so moving one
 * would change nothing the administrator could see and the control is not
 * offered for it.
 *
 * `locked` names a role mapping this caller may not edit — the owner's own,
 * above what an organisation's administrator may give by hand (Directory.tsx's
 * isLocked). Such a row is skipped over exactly like a branch row: never the
 * one moved, and never the one swapped into, because a caller without
 * tenant.manage changing where it sits in the order is still changing
 * something about a mapping that is not theirs to touch. Absent, nothing is
 * ever locked — the owner, who may edit every row, calls it that way.
 *
 * Returns the same array (by reference) when the row cannot move — already at
 * that end of the movable role mappings — so a caller can tell "nothing to
 * send" from "send this".
 */
export function moveMappingRow(
  rows: MappingRow[],
  key: string,
  direction: 'up' | 'down',
  locked: (row: MappingRow) => boolean = () => false,
): MappingRow[] {
  const movable = (row: MappingRow) => row.gives === 'role' && !locked(row)
  const from = rows.findIndex((r) => r.key === key)
  if (from < 0 || !movable(rows[from]!)) return rows
  const step = direction === 'up' ? -1 : 1
  let to = from + step
  while (to >= 0 && to < rows.length && !movable(rows[to]!)) to += step
  if (to < 0 || to >= rows.length) return rows
  const next = rows.slice()
  ;[next[from], next[to]] = [next[to]!, next[from]!]
  return next
}

/**
 * connectionProblem mirrors directory.Config.Problems for the one relationship
 * worth catching before a save round-trips: an ldap:// address without StartTLS
 * sends every password across the network in the clear. Everything else Problems
 * checks — the search base, the filter, the service account against the sign-in
 * template — is left to the server's own answer, shown as it is refused.
 *
 * English, and takes no language: only the installation's owner edits the
 * connection, on a card that stays English (admin/DirectoryOwner.tsx).
 */
export function connectionProblem(url: string, startTls: boolean): string | undefined {
  let scheme: string
  try {
    scheme = new URL(url).protocol.replace(/:$/, '')
  } catch {
    return undefined
  }
  if (scheme === 'ldap' && !startTls) {
    return 'An ldap:// address needs StartTLS switched on; without it, passwords cross the network in clear.'
  }
  return undefined
}

/**
 * afterSignOut is where the browser goes once the session cookie is cleared: the
 * proxy's own sign-out address for a session the proxy opened, and nowhere — the
 * sign-in screen, as usual — for everybody else.
 *
 * Restricted to an absolute http(s) address on purpose. The value comes from this
 * installation's own configuration file (auth.proxy.sign_out_url), not from
 * anything a browser sent, but a stray relative path or a javascript: value
 * reaching window.location.assign is not a mistake to render — it is one to
 * refuse.
 */
export function afterSignOut(identity: Pick<Identity, 'signOutUrl'> | null | undefined): string | undefined {
  const url = identity?.signOutUrl
  if (url && (url.startsWith('https://') || url.startsWith('http://'))) return url
  return undefined
}
